"use client";

import { useEffect, useState } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { PDFDownloadLink } from "@react-pdf/renderer";
import Layout from "../../layout/Layout";
import Loading from "../../common/Loading";
import { BackIcon, DownloadNarrowIcon } from "../../icons";
import { cn } from "../../../utils/classnames";
import ConfirmationModal from "../components/ConfirmationModal";
import {
  getInvoiceDetail,
  chargeInvoice,
  updateInvoicePaymentStatus,
  getInvoicePeriods,
} from "../../../actions/admin/invoice";
import InvoiceInformation from "./_components/InvoiceInformation";
import InvoiceItemsDetail from "./_components/InvoiceItemsDetail";
import InvoiceDocument from "./_components/pdf/InvoiceDocument";

const STATUS_MAP = {
  PAID: { label: "Paid", className: "tw:bg-[#3CAEA3]" },
  CHARGE_PENDING: { label: "Charge Pending", className: "tw:bg-[#F6BB2A]" },
  UNPAID: { label: "Unpaid", className: "tw:bg-[#EA4335]" },
};

const InvoiceDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    invoiceDetail,
    loadingDetail,
    charging,
    chargeError,
    updatingStatus,
    statusError,
    periods,
  } = useSelector((state) => state.invoice);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  useEffect(() => {
    dispatch(getInvoiceDetail(id));
  }, [dispatch, id]);

  // Load the account's other invoice periods for the period switcher.
  const accountId = invoiceDetail?.user?._id;
  useEffect(() => {
    if (accountId) dispatch(getInvoicePeriods(accountId));
  }, [dispatch, accountId]);

  const handleSelectPeriod = (periodInvoiceId) => {
    if (periodInvoiceId && periodInvoiceId !== id) {
      history.push(`/admin/invoices/${periodInvoiceId}`);
    }
  };

  const activeCard = invoiceDetail?.user?.businessDetail?.activePaymentMethod;
  const canCharge = invoiceDetail?.paymentStatus === "UNPAID" && !!activeCard;

  const handleCharge = () => {
    if (charging) return;
    setShowChargeModal(false);
    dispatch(chargeInvoice(id));
  };

  const handleConfirmUpdateStatus = () => {
    if (updatingStatus || !pendingStatus) return;
    const nextStatus = pendingStatus;
    setPendingStatus(null);
    dispatch(updateInvoicePaymentStatus(id, nextStatus));
  };

  if (loadingDetail || !invoiceDetail) {
    return (
      <Layout>
        <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 tw:pb-[42px] w-100">
          <Loading />
        </main>
      </Layout>
    );
  }

  const normalizedStatus = String(invoiceDetail?.paymentStatus || "UNPAID").toUpperCase();
  const statusMeta = STATUS_MAP[normalizedStatus] || STATUS_MAP.UNPAID;
  const businessName =
    invoiceDetail?.user?.businessDetail?.businessInformation?.businessName ||
    invoiceDetail?.user?.name ||
    "-";
  const periodText = moment(
    `${invoiceDetail?.year}-${String(invoiceDetail?.month).padStart(2, "0")}-01`
  )
    .locale("en")
    .format("MMMM YYYY");

  return (
    <Layout>
      <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 tw:pb-[42px] w-100">
        <div className="tw:font-hanken-grotesk tw:text-[#212529]">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6 tw:pb-[28px] tw:lg:pb-[42px] tw:px-5 tw:pt-8">
            <Link
              to="/admin/invoices"
              className="tw:inline-flex tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[#282828] tw:!no-underline"
            >
              <BackIcon color="#8D8D8D" width={7} height={10} />
              <span className="tw:!font-hanken-grotesk tw:text-[#282828] tw:text-[14px]">Back</span>
            </Link>

            <div className="tw:flex tw:flex-1 tw:items-center tw:justify-between tw:gap-4">
              <div className="tw:flex tw:flex-col">
                <div className="tw:!font-hanken-grotesk tw:font-[500] tw:text-[14px] tw:text-[#7c7b7b]">
                  {`[${businessName}・${periodText}]`}
                </div>
                <div className="tw:!font-hanken-grotesk tw:text-[24px] tw:font-[500] tw:leading-normal tw:text-[#212529]">
                  Invoice Detail
                </div>
              </div>

              <div className="tw:flex tw:items-center tw:gap-3">
                <PDFDownloadLink
                  document={<InvoiceDocument invoice={invoiceDetail} />}
                  fileName={`TLF-INVOICE-${businessName}-${periodText}.pdf`}
                  style={{ textDecoration: "none" }}
                >
                  {({ loading }) => (
                    <span className="tw:inline-flex tw:h-[45px] tw:items-center tw:justify-center tw:gap-[10px] tw:whitespace-nowrap tw:rounded-[5px] tw:bg-[#282828] tw:px-6 tw:text-[15px] tw:leading-[22px] tw:text-white">
                      <DownloadNarrowIcon />
                      {loading ? "Preparing..." : "Download PDF"}
                    </span>
                  )}
                </PDFDownloadLink>

                <span
                  className={cn(
                    "tw:inline-flex tw:h-[45px] tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-[5px] tw:px-6 tw:text-[15px] tw:leading-[22px] tw:text-white",
                    statusMeta.className
                  )}
                >
                  {statusMeta.label}
                </span>
              </div>
            </div>
          </div>

          <div className="tw:px-5">
            <InvoiceInformation
              invoice={invoiceDetail}
              activeCard={activeCard}
              canCharge={canCharge}
              charging={charging}
              chargeError={chargeError}
              onCharge={() => setShowChargeModal(true)}
              onUpdateStatus={(status) => setPendingStatus(status)}
              updatingStatus={updatingStatus}
              statusError={statusError}
              periods={periods}
              currentInvoiceId={id}
              onSelectPeriod={handleSelectPeriod}
            />

            <InvoiceItemsDetail items={invoiceDetail.items} />
          </div>
        </div>

        <ConfirmationModal
          show={showChargeModal}
          title={`Charge ${(invoiceDetail?.totalAmount || 0).toLocaleString()} JPY to ${
            activeCard?.brand || ""
          } ${activeCard?.cardNumber || ""}?`}
          description="This charges the whole monthly invoice in one payment."
          confirmLabel="Charge"
          busyLabel="Charging…"
          isBusy={charging}
          onCancel={() => setShowChargeModal(false)}
          onConfirm={handleCharge}
        />

        <ConfirmationModal
          show={!!pendingStatus}
          title={`Set this invoice's payment status to "${
            STATUS_MAP[pendingStatus]?.label || pendingStatus
          }"?`}
          description="Use this when the invoice was settled outside the system, such as by bank transfer."
          confirmLabel="Update"
          busyLabel="Updating…"
          isBusy={updatingStatus}
          onCancel={() => setPendingStatus(null)}
          onConfirm={handleConfirmUpdateStatus}
        />
      </main>
    </Layout>
  );
};

export default InvoiceDetail;
